import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { apiClient } from '@/lib/api';
import { getAccessToken, getStoredUserRaw } from '@/lib/authStorage';

export type ProjectUiMode = 'normal' | 'experienced' | 'pro';

type ProjectUiModeState = {
  mode: ProjectUiMode;
  synced: boolean;
  setMode: (mode: ProjectUiMode) => void;
  syncFromServer: () => Promise<void>;
};

// Сохраняем режим в БД (для авторизованных)
async function saveToServer(mode: ProjectUiMode): Promise<void> {
  try {
    const token = getAccessToken();
    if (!token) return;
    let currentPrefs: Record<string, any> = {};
    try {
      const raw = getStoredUserRaw();
      const user = raw ? JSON.parse(raw) : null;
      if (user?.preferences && typeof user.preferences === 'object') {
        currentPrefs = user.preferences;
      }
    } catch {}
    await apiClient.updateProfile({ preferences: { ...currentPrefs, project_ui_mode: mode } });
  } catch {
    // Не блокируем UI при ошибке сохранения
  }
}

export const useProjectUiModeStore = create<ProjectUiModeState>()(
  persist(
    (set, get) => ({
      mode: 'normal',
      synced: false,
      setMode: (mode) => {
        set({ mode });
        // Сохраняем на сервер асинхронно
        void saveToServer(mode);
      },
      syncFromServer: async () => {
        // Не синхронизируем повторно
        if (get().synced) return;
        try {
          const token = getAccessToken();
          if (!token) {
            set({ synced: true });
            return;
          }
          const user = await apiClient.getCurrentUser();
          const serverMode = (user?.preferences as Record<string, any>)?.project_ui_mode;
          if (serverMode && ['normal', 'experienced', 'pro'].includes(serverMode)) {
            set({ mode: serverMode as ProjectUiMode, synced: true });
          } else {
            set({ synced: true });
          }
        } catch {
          set({ synced: true });
        }
      },
    }),
    {
      name: 'lisad_project_ui_mode',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ mode: s.mode }),
      // Гидратацию делаем вручную, чтобы не ловить SSR-мисматчи.
      skipHydration: true,
    }
  )
);

export function rehydrateProjectUiMode() {
  if (typeof window === 'undefined') return;
  useProjectUiModeStore.persist.rehydrate();
}
